import { Vec3 } from '@viral-space/shared';
import {
  getInputSettings,
  effectiveMouseSensitivity,
  effectiveTouchLookRate,
  BASE_TOUCH_LOOK_RATE,
} from './settings';

export interface InputState {
  thrust: Vec3;
  rotation: Vec3;
  isBoosting: boolean;
  isFiring: boolean;
  weaponSlot: number;
  pointerLocked: boolean;
  mouseX: number;
  mouseY: number;
}

interface TouchJoystick {
  active: boolean;
  identifier: number;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

/**
 * External touch source (implemented by `TouchControls`). When set via
 * `setTouchSource`, the InputManager's built-in canvas touch handling is
 * disabled and per-frame input is driven by the overlay joysticks + the
 * overlay's virtual fire/boost buttons instead. This keeps the two input
 * surfaces from fighting each other on mobile.
 */
export interface TouchInputSource {
  /**
   * Normalized thrust vector from the overlay. `x` = strafe right,
   * `y` = vertical (up/down) thrust driven by on-screen UP/DN buttons,
   * `z` = forward/back from the left stick (world space).
   */
  getThrust(): { x: number; y: number; z: number };
  getRotation(): { x: number; y: number };
  isFiring(): boolean;
  isBoosting(): boolean;
  update(): void;
}

const DEADZONE = 0.15;

// Maximum angular velocity (radians per second) the right touch stick
// produces at full deflection. Below this is too sluggish to fight,
// above this becomes the "flicks 360° from a thumb tap" behavior the
// pre-fix mobile build had. ~1.8 rad/s ≈ 103°/s — a bit slower than a
// console twin-stick default, which matches the game's slow "capital
// ship" feel better than the 2.5 rad/s first-pass value.
// Max radians/sec of yaw (or pitch) the right touch stick can command
// at full deflection BEFORE the user's sensitivity multiplier is applied.
// Pair this with the response curve in `TouchControls.getRotation()`.
// History: 1.5 (too slow → 86°/sec) → 3.6 (too fast → 206°/sec, hard to
// settle on a target) → 2.7 (~155°/sec at full deflection, ~58°/sec at
// 50%). The slight reduction gives finer control during combat without
// losing the responsive feel; the new mobile-only aim assist (see
// `applyMobileAimAssist` in main.ts) compensates for the lost snap-onto-
// target speed by gently tugging the crosshair toward the current
// target's center when it's close, so tracking moving NPCs doesn't
// require pixel-perfect stick control.
//
// The actual per-frame rate used by `getState()` is this base value
// multiplied by `settings.touchSensitivity` (see `effectiveTouchLookRate`
// in settings.ts). Default touchSensitivity is 0.7, bringing full-
// deflection rate to ~108°/sec on mobile (closer to a console twin-
// stick default and addressing the "too sensitive on phones" report).

/**
 * Live accessor for the currently effective max touch look-rate
 * (rad/sec at full deflection). Reads the user's tunable
 * `touchSensitivity` every call so toggling the slider takes effect
 * the next frame. Exposed for callers like the mobile aim-assist in
 * main.ts that need to size their gain by what fraction of the stick's
 * angular budget the pilot is currently using.
 */
export function getEffectiveTouchLookRate(): number {
  return effectiveTouchLookRate(getInputSettings());
}

/**
 * Re-export of the unscaled baseline so other call sites that want the
 * "stock" rate (not the user-tuned one) don't have to pull from
 * `./settings`. Kept here for backwards-compat with anything that used
 * to import the old `TOUCH_LOOK_RATE_REF` constant.
 */
export const TOUCH_LOOK_RATE_REF = BASE_TOUCH_LOOK_RATE;

function applyDeadzone(value: number): number {
  if (Math.abs(value) < DEADZONE) return 0;
  const sign = Math.sign(value);
  return sign * ((Math.abs(value) - DEADZONE) / (1 - DEADZONE));
}

export class InputManager {
  private keys = new Set<string>();
  private mouseX = 0;
  private mouseY = 0;
  private mouseDeltaX = 0;
  private mouseDeltaY = 0;
  private mouseButtons = new Set<number>();
  private pointerLocked = false;
  private weaponSlot = 0;

  private leftJoystick: TouchJoystick = { active: false, identifier: -1, startX: 0, startY: 0, currentX: 0, currentY: 0 };
  private rightJoystick: TouchJoystick = { active: false, identifier: -1, startX: 0, startY: 0, currentX: 0, currentY: 0 };
  private touchFiring = false;
  private touchBoosting = false;

  /**
   * When set, the InputManager uses this overlay-driven source for touch
   * thrust/aim/fire/boost instead of the internal split-screen canvas
   * handler. See `setTouchSource`.
   */
  private touchSource: TouchInputSource | null = null;

  private gamepadIndex: number | null = null;
  private gamepadThrust: Vec3 = { x: 0, y: 0, z: 0 };
  private gamepadRotation: Vec3 = { x: 0, y: 0, z: 0 };
  private gamepadFire = false;
  private gamepadBoost = false;

  // Timestamp (ms) of the previous getState() call. Used to derive a
  // dt for the touch / gamepad rotation so the look speed is
  // independent of frame rate (otherwise full stick deflection rotated
  // the camera by ~120 rad/s on a 60Hz device — basically uncontrollable).
  private _lastInputTime = 0;

  private canvas: HTMLElement;
  private disposed = false;
  private _enabled = false;

  /**
   * Optional listener that fires when pointer lock exits while the
   * input manager is still enabled — i.e. the lock was lost without
   * us asking for it. The browser swallows the ESC keydown event
   * during pointer lock (Chrome especially) so the application's own
   * keyboard handler never sees the first press; the listener gives
   * `main.ts` a hook to open the pause menu on that first ESC instead
   * of waiting for the second one. Set via {@link setPointerLockExitCallback}.
   */
  private _pointerLockExitCb: (() => void) | null = null;

  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onMouseMove: (e: MouseEvent) => void;
  private readonly onMouseDown: (e: MouseEvent) => void;
  private readonly onMouseUp: (e: MouseEvent) => void;
  private readonly onClick: (e: MouseEvent) => void;
  private readonly onPointerLockChange: () => void;
  private readonly onWheel: (e: WheelEvent) => void;
  private readonly onTouchStart: (e: TouchEvent) => void;
  private readonly onTouchMove: (e: TouchEvent) => void;
  private readonly onTouchEnd: (e: TouchEvent) => void;
  private readonly onGamepadConnected: (e: GamepadEvent) => void;
  private readonly onGamepadDisconnected: (e: GamepadEvent) => void;
  private readonly onContextMenu: (e: Event) => void;
  private readonly onBlur: () => void;

  constructor(canvas: HTMLElement) {
    this.canvas = canvas;

    this.onKeyDown = this.handleKeyDown.bind(this);
    this.onKeyUp = this.handleKeyUp.bind(this);
    this.onMouseMove = this.handleMouseMove.bind(this);
    this.onMouseDown = this.handleMouseDown.bind(this);
    this.onMouseUp = this.handleMouseUp.bind(this);
    this.onClick = this.handleClick.bind(this);
    this.onPointerLockChange = this.handlePointerLockChange.bind(this);
    this.onWheel = this.handleWheel.bind(this);
    this.onTouchStart = this.handleTouchStart.bind(this);
    this.onTouchMove = this.handleTouchMove.bind(this);
    this.onTouchEnd = this.handleTouchEnd.bind(this);
    this.onGamepadConnected = this.handleGamepadConnected.bind(this);
    this.onGamepadDisconnected = this.handleGamepadDisconnected.bind(this);
    this.onContextMenu = (e: Event) => e.preventDefault();
    this.onBlur = () => {
      this.keys.clear();
      this.mouseButtons.clear();
      this.mouseDeltaX = 0;
      this.mouseDeltaY = 0;
    };

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    canvas.addEventListener('click', this.onClick);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    window.addEventListener('wheel', this.onWheel, { passive: false });
    canvas.addEventListener('touchstart', this.onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this.onTouchEnd);
    canvas.addEventListener('touchcancel', this.onTouchEnd);
    window.addEventListener('gamepadconnected', this.onGamepadConnected);
    window.addEventListener('gamepaddisconnected', this.onGamepadDisconnected);
    canvas.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('visibilitychange', this.onBlur);
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this._enabled) return;
    if (e.code === 'Escape') return;
    this.keys.add(e.code);
    if (e.code >= 'Digit1' && e.code <= 'Digit9') {
      this.weaponSlot = parseInt(e.code.replace('Digit', ''), 10) - 1;
    }
  }

  private handleKeyUp(e: KeyboardEvent): void {
    this.keys.delete(e.code);
  }

  private handleMouseMove(e: MouseEvent): void {
    // Always track cursor position for UI overlays, but only accumulate
    // look-deltas for the ship while input is enabled AND pointer locked.
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;
    if (!this._enabled) return;
    if (this.pointerLocked) {
      this.mouseDeltaX += e.movementX;
      this.mouseDeltaY += e.movementY;
    }
  }

  private handleMouseDown(e: MouseEvent): void {
    // When a UI window/menu is open the input manager is disabled; mouse
    // clicks must not enter the gameplay input state (otherwise clicks on
    // UI buttons would still register as "fire").
    if (!this._enabled) return;
    this.mouseButtons.add(e.button);
  }

  private handleMouseUp(e: MouseEvent): void {
    // Always clear on mouseup so a button can't get "stuck" if it was
    // pressed while enabled and released after the window re-disabled input.
    this.mouseButtons.delete(e.button);
  }

  enable(): void {
    this._enabled = true;
  }

  disable(): void {
    this._enabled = false;
    if (document.pointerLockElement === this.canvas) {
      document.exitPointerLock();
    }
    this.keys.clear();
    this.mouseButtons.clear();
    this.mouseDeltaX = 0;
    this.mouseDeltaY = 0;
    this.pointerLocked = false;
    // Reset touch joysticks and virtual buttons so a held touch can't
    // continue driving the ship while a UI window is open.
    this.leftJoystick.active = false;
    this.leftJoystick.identifier = -1;
    this.rightJoystick.active = false;
    this.rightJoystick.identifier = -1;
    this.touchFiring = false;
    this.touchBoosting = false;
    // Gamepad button values will be re-polled from the hardware next frame,
    // but `getState()` also short-circuits to a neutral state while disabled.
  }

  get enabled(): boolean {
    return this._enabled;
  }

  private handleClick(_e: MouseEvent): void {
    if (!this._enabled) return;
    this.tryLockPointer();
  }

  /**
   * Request pointer lock on the game canvas, feature-detecting the API
   * and swallowing rejections. Shared by the canvas click handler and
   * {@link acquirePointerLock} (called when the pause menu closes).
   *
   * iOS / Capacitor WKWebView doesn't implement the Pointer Lock API at
   * all — `canvas.requestPointerLock` is `undefined` and calling it throws
   * a TypeError that would interrupt the rest of the click pipeline. The
   * returned value can be `undefined` (some Firefox versions) or a Promise
   * (Chrome); either way we don't care about the result here.
   */
  private tryLockPointer(): void {
    if (this.pointerLocked) return;
    const req = (this.canvas as HTMLCanvasElement & {
      requestPointerLock?: (opts?: unknown) => unknown;
    }).requestPointerLock;
    if (typeof req !== 'function') return;
    try {
      const r = req.call(this.canvas);
      if (r && typeof (r as Promise<unknown>).catch === 'function') {
        (r as Promise<unknown>).catch(() => {
          // Lock rejection (e.g. not triggered by user gesture, the
          // browser's post-Esc cooldown, or an unsupported runtime) is
          // not a gameplay-visible problem — the pilot can still click
          // the canvas to lock. Swallow so it doesn't spam devtools.
        });
      }
    } catch {
      // Some runtimes surface the unsupported case as a throw rather
      // than a missing property; treat identically.
    }
  }

  /**
   * Re-acquire pointer lock without waiting for the player to click the
   * canvas again. Called when the pause menu is dismissed (web + desktop)
   * so closing the menu hands mouse-look straight back. MUST be invoked
   * from within a user-gesture handler (e.g. the "return to game" button
   * click) — the browser blocks pointer lock requested outside one. If
   * the gesture has lapsed (e.g. dismissed via the Esc key during the
   * browser's post-Esc cooldown) the request simply fails and the player
   * falls back to clicking the canvas.
   */
  acquirePointerLock(): void {
    if (!this._enabled) return;
    this.tryLockPointer();
  }

  private handlePointerLockChange(): void {
    const wasLocked = this.pointerLocked;
    this.pointerLocked = document.pointerLockElement === this.canvas;
    if (this.pointerLocked) return;

    this.mouseDeltaX = 0;
    this.mouseDeltaY = 0;

    // If we lost the lock while still enabled then the user pressed ESC
    // (or the browser yanked the lock for some other reason — alt-tab,
    // window blur, etc.) and we should treat that as a "open the pause
    // menu" intent. `disable()` always sets `_enabled = false` BEFORE
    // calling `exitPointerLock()`, so any time the flag is still true
    // here the exit was not initiated by us and the consumer can react.
    if (wasLocked && this._enabled && this._pointerLockExitCb) {
      this._pointerLockExitCb();
    }
  }

  /**
   * Register (or clear) a one-shot consumer for unsolicited pointer-lock
   * exits. See {@link _pointerLockExitCb} for the why. Pass `null` to
   * remove the current handler.
   */
  setPointerLockExitCallback(cb: (() => void) | null): void {
    this._pointerLockExitCb = cb;
  }

  private handleWheel(e: WheelEvent): void {
    if (!this._enabled) return;
    e.preventDefault();
    const dir = Math.sign(e.deltaY);
    this.weaponSlot = Math.max(0, Math.min(8, this.weaponSlot + dir));
  }

  /**
   * Advance the selected weapon slot by +1 or -1 with wrap-around.
   * Exposed for touch/virtual-button callers that don't have a wheel.
   */
  cycleWeaponSlot(dir: 1 | -1 = 1): void {
    const next = this.weaponSlot + dir;
    const wrapped = next < 0 ? 8 : next > 8 ? 0 : next;
    this.weaponSlot = wrapped;
  }

  setTouchSource(source: TouchInputSource | null): void {
    this.touchSource = source;
    // Clear any in-flight canvas joystick state so a leftover finger from
    // before the hand-over doesn't keep feeding the ship.
    this.leftJoystick.active = false;
    this.rightJoystick.active = false;
    this.touchFiring = false;
    this.touchBoosting = false;
  }

  private handleTouchStart(e: TouchEvent): void {
    if (!this._enabled) return;
    // When an overlay-based touch source is attached, the overlay itself
    // already handles touches (with preventDefault in its own listeners);
    // we must not double-interpret them here.
    if (this.touchSource) return;
    e.preventDefault();
    const screenMid = window.innerWidth / 2;

    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const isLeftSide = touch.clientX < screenMid;

      if (isLeftSide && !this.leftJoystick.active) {
        this.leftJoystick = {
          active: true,
          identifier: touch.identifier,
          startX: touch.clientX,
          startY: touch.clientY,
          currentX: touch.clientX,
          currentY: touch.clientY,
        };
      } else if (!isLeftSide && !this.rightJoystick.active) {
        this.rightJoystick = {
          active: true,
          identifier: touch.identifier,
          startX: touch.clientX,
          startY: touch.clientY,
          currentX: touch.clientX,
          currentY: touch.clientY,
        };
      }

      const bottomY = window.innerHeight;
      const btnSize = 80;
      if (touch.clientX > screenMid + 100 && touch.clientY > bottomY - btnSize * 2) {
        this.touchFiring = true;
      }
      if (touch.clientX < screenMid - 100 && touch.clientY > bottomY - btnSize * 2) {
        this.touchBoosting = true;
      }
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this._enabled) return;
    if (this.touchSource) return;
    e.preventDefault();
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      if (touch.identifier === this.leftJoystick.identifier) {
        this.leftJoystick.currentX = touch.clientX;
        this.leftJoystick.currentY = touch.clientY;
      } else if (touch.identifier === this.rightJoystick.identifier) {
        this.rightJoystick.currentX = touch.clientX;
        this.rightJoystick.currentY = touch.clientY;
      }
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (this.touchSource) return;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      if (touch.identifier === this.leftJoystick.identifier) {
        this.leftJoystick.active = false;
        this.leftJoystick.identifier = -1;
      } else if (touch.identifier === this.rightJoystick.identifier) {
        this.rightJoystick.active = false;
        this.rightJoystick.identifier = -1;
      }
    }
    if (e.touches.length === 0) {
      this.touchFiring = false;
      this.touchBoosting = false;
    }
  }

  private handleGamepadConnected(e: GamepadEvent): void {
    this.gamepadIndex = e.gamepad.index;
  }

  private handleGamepadDisconnected(e: GamepadEvent): void {
    if (this.gamepadIndex === e.gamepad.index) {
      this.gamepadIndex = null;
      this.gamepadThrust = { x: 0, y: 0, z: 0 };
      this.gamepadRotation = { x: 0, y: 0, z: 0 };
      this.gamepadFire = false;
      this.gamepadBoost = false;
    }
  }

  private pollGamepad(): void {
    if (this.gamepadIndex === null) return;
    const gamepads = navigator.getGamepads();
    const gp = gamepads[this.gamepadIndex];
    if (!gp) return;

    const lx = applyDeadzone(gp.axes[0] ?? 0);
    const ly = applyDeadzone(gp.axes[1] ?? 0);
    const rx = applyDeadzone(gp.axes[2] ?? 0);
    const ry = applyDeadzone(gp.axes[3] ?? 0);

    this.gamepadThrust = { x: lx, y: 0, z: -ly };
    this.gamepadRotation = { x: -ry, y: -rx, z: 0 };

    const leftTrigger = gp.buttons[6]?.value ?? 0;
    const rightTrigger = gp.buttons[7]?.value ?? 0;
    this.gamepadFire = rightTrigger > 0.1;
    this.gamepadBoost = leftTrigger > 0.1;
  }

  private getJoystickVector(joystick: TouchJoystick, maxRadius: number): { x: number; y: number } {
    if (!joystick.active) return { x: 0, y: 0 };
    const dx = joystick.currentX - joystick.startX;
    const dy = joystick.currentY - joystick.startY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const clampedDist = Math.min(dist, maxRadius);
    if (dist < 1) return { x: 0, y: 0 };
    return {
      x: (dx / dist) * (clampedDist / maxRadius),
      y: (dy / dist) * (clampedDist / maxRadius),
    };
  }

  private _lastState: InputState | null = null;

  update(): void {
    if (this.disposed) return;
    this.pollGamepad();
    this.touchSource?.update();
  }

  /** Read-only snapshot of the most recent state (does not consume mouse deltas). */
  peekState(): InputState | null {
    return this._lastState;
  }

  getState(): InputState {
    // When input is disabled (UI window/menu open, docked, paused, tutorial,
    // etc.) return a fully neutral state so nothing the player could do with
    // the mouse, keyboard, gamepad, or touch translates into ship commands.
    if (!this._enabled) {
      // Reset the dt baseline so the first frame after re-enabling
      // doesn't apply the time spent in a menu/dock as a single huge
      // touch-look impulse.
      this._lastInputTime = 0;
      this._lastState = {
        thrust: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        isBoosting: false,
        isFiring: false,
        weaponSlot: this.weaponSlot,
        pointerLocked: this.pointerLocked,
        mouseX: this.mouseX,
        mouseY: this.mouseY,
      };
      return this._lastState;
    }

    const kbThrust: Vec3 = { x: 0, y: 0, z: 0 };
    if (this.keys.has('KeyW')) kbThrust.z += 1;
    if (this.keys.has('KeyS')) kbThrust.z -= 1;
    if (this.keys.has('KeyA')) kbThrust.x -= 1;
    if (this.keys.has('KeyD')) kbThrust.x += 1;
    if (this.keys.has('Space')) kbThrust.y += 1;
    if (this.keys.has('KeyC') || this.keys.has('ControlLeft') || this.keys.has('ControlRight')) kbThrust.y -= 1;

    // Prefer the overlay-driven touch source when set; otherwise fall
    // back to the legacy split-screen canvas joysticks for devices that
    // never get the mobile overlay wired up. The overlay reports thrust
    // as {x, z} (world-space style) and rotation already multiplied by
    // the per-axis look gain; we reshape both back into the normalized
    // {x, y} joystick vectors so we can apply our own dt-scaled gain
    // below.
    const overlayThrust = this.touchSource?.getThrust() ?? null;
    const overlayRotation = this.touchSource?.getRotation() ?? null;
    const touchMove = overlayThrust
      ? { x: overlayThrust.x, y: -overlayThrust.z }
      : this.getJoystickVector(this.leftJoystick, 80);
    // Overlay getRotation returns (-dy*2, -dx*2). Reverse the *2 so
    // touchAim is the raw joystick deflection in [-1, 1].
    const touchAim = overlayRotation
      ? { x: -overlayRotation.y * 0.5, y: -overlayRotation.x * 0.5 }
      : this.getJoystickVector(this.rightJoystick, 80);

    // dt since the previous getState(), clamped so a backgrounded /
    // long-paused tab doesn't dump a multi-second look impulse on the
    // first frame after returning. The first call gets a 60Hz baseline.
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const dt = this._lastInputTime > 0
      ? Math.min((now - this._lastInputTime) / 1000, 0.1)
      : 1 / 60;
    this._lastInputTime = now;

    // Read the live preferences each frame so a toggle / slider drag in
    // the options menu takes effect immediately (no need to restart the
    // session). The lookup is a single map read so the per-frame cost
    // is trivial.
    const settings = getInputSettings();
    const mouseYSign = settings.invertYMouse ? 1 : -1;
    const touchYSign = settings.invertYTouch ? 1 : -1;

    // Both sensitivity coefficients fold in the user's tunable
    // multiplier (1.0 = stock). The default touch value is < 1 so
    // mobile starts out less twitchy than the raw base rate.
    const sensitivity = effectiveMouseSensitivity(settings);
    const touchLookGain = effectiveTouchLookRate(settings) * dt;

    const kbRotation: Vec3 = {
      x: mouseYSign * this.mouseDeltaY * sensitivity,
      y: -this.mouseDeltaX * sensitivity,
      z: 0,
    };

    // Vertical thrust on mobile comes from dedicated UP/DN buttons
    // exposed via the overlay's getThrust().y — keyboard still uses
    // Space / Ctrl, gamepad still uses its own mapping.
    const touchVertical = overlayThrust ? overlayThrust.y : 0;

    const thrust: Vec3 = {
      x: clamp(kbThrust.x + this.gamepadThrust.x + touchMove.x, -1, 1),
      y: clamp(kbThrust.y + this.gamepadThrust.y + touchVertical, -1, 1),
      z: clamp(kbThrust.z + this.gamepadThrust.z - touchMove.y, -1, 1),
    };

    // The gamepad path also uses `mouseYSign` (rather than a separate
    // `invertYGamepad`) so a single "invert Y" toggle covers every
    // pointer-style look surface on the same physical device — desktop
    // pilots that fly with both an Xbox stick and the mouse never see
    // them disagree.
    const rotation: Vec3 = {
      x: clamp(kbRotation.x + (mouseYSign * -this.gamepadRotation.x) + (touchYSign * touchAim.y * touchLookGain), -Math.PI, Math.PI),
      y: clamp(kbRotation.y + this.gamepadRotation.y + (-touchAim.x * touchLookGain), -Math.PI, Math.PI),
      z: kbRotation.z + this.gamepadRotation.z,
    };

    const touchBoostingNow = this.touchSource
      ? this.touchSource.isBoosting()
      : this.touchBoosting;
    const touchFiringNow = this.touchSource
      ? this.touchSource.isFiring()
      : this.touchFiring;

    const isBoosting =
      this.keys.has('ShiftLeft') ||
      this.keys.has('ShiftRight') ||
      this.gamepadBoost ||
      touchBoostingNow;

    const isFiring =
      this.mouseButtons.has(0) ||
      this.gamepadFire ||
      touchFiringNow;

    this.mouseDeltaX = 0;
    this.mouseDeltaY = 0;

    this._lastState = {
      thrust,
      rotation,
      isBoosting,
      isFiring,
      weaponSlot: this.weaponSlot,
      pointerLocked: this.pointerLocked,
      mouseX: this.mouseX,
      mouseY: this.mouseY,
    };

    return this._lastState;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.canvas.removeEventListener('click', this.onClick);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    window.removeEventListener('wheel', this.onWheel);
    this.canvas.removeEventListener('touchstart', this.onTouchStart);
    this.canvas.removeEventListener('touchmove', this.onTouchMove);
    this.canvas.removeEventListener('touchend', this.onTouchEnd);
    this.canvas.removeEventListener('touchcancel', this.onTouchEnd);
    window.removeEventListener('gamepadconnected', this.onGamepadConnected);
    window.removeEventListener('gamepaddisconnected', this.onGamepadDisconnected);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('visibilitychange', this.onBlur);

    if (this.pointerLocked) {
      document.exitPointerLock();
    }
  }
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
